import {findNearestCity} from '../data/philippineCities';
import {generateVolcanicActivity} from '../data/volcanoes';

const PH_BOUNDS={
  minlatitude: 4.5,
  maxlatitude: 21.5,
  minlongitude: 116,
  maxlongitude: 127.5
};

// Enhanced caching with background refresh
const CACHE_CONFIG={
  DURATION: {
    USGS: 30000, // 30 seconds for earthquakes
    GDACS: 60000, // 1 minute for global disasters
    VOLCANIC: 120000, // 2 minutes for volcanic data
    TIDAL: 300000, // 5 minutes for tidal data
    FLOOD: 180000, // 3 minutes for flood data
    PAGASA: 90000 // 1.5 minutes for weather
  },
  MAX_SIZE: 100, // Maximum cache entries
  BACKGROUND_REFRESH: true // Enable background refresh
};

// Cache with metadata
class Cache {
  constructor() {
    this.data=new Map();
    this.timestamps=new Map();
    this.refreshTimers=new Map();
  }

  set(key,value,duration=60000) {
    // Clean old entries if cache is full
    if (this.data.size >=CACHE_CONFIG.MAX_SIZE) {
      this.cleanup();
    }
    this.data.set(key,value);
    this.timestamps.set(key,Date.now());
    
    // Schedule background refresh
    if (CACHE_CONFIG.BACKGROUND_REFRESH) {
      this.scheduleRefresh(key,duration);
    }
  }

  get(key) {
    const timestamp=this.timestamps.get(key);
    if (!timestamp) return null;

    const age=Date.now() - timestamp;
    const duration=this.getDuration(key);

    // Return data even if expired,but mark for refresh
    if (age < duration * 3) { // Allow stale data for 3x duration
      if (age > duration) {
        this.scheduleRefresh(key,duration);
      }
      return this.data.get(key);
    }
    
    // Data is too old,remove it
    this.delete(key);
    return null;
  }

  delete(key) {
    this.data.delete(key);
    this.timestamps.delete(key);
    if (this.refreshTimers.has(key)) {
      clearTimeout(this.refreshTimers.get(key));
      this.refreshTimers.delete(key);
    }
  }

  cleanup() {
    const now=Date.now();
    const keysToDelete=[];
    for (const [key,timestamp] of this.timestamps) {
      const age=now - timestamp;
      const duration=this.getDuration(key);
      if (age > duration * 5) { // Remove very old entries
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key=> this.delete(key));
  }

  getDuration(key) {
    if (key.startsWith('usgs_')) return CACHE_CONFIG.DURATION.USGS;
    if (key.startsWith('gdacs_')) return CACHE_CONFIG.DURATION.GDACS;
    if (key.startsWith('volcanic_')) return CACHE_CONFIG.DURATION.VOLCANIC;
    if (key.startsWith('tidal_')) return CACHE_CONFIG.DURATION.TIDAL;
    if (key.startsWith('flood_')) return CACHE_CONFIG.DURATION.FLOOD;
    if (key.startsWith('pagasa_')) return CACHE_CONFIG.DURATION.PAGASA;
    return 60000;
  }
  
  scheduleRefresh(key,duration) {
    if (this.refreshTimers.has(key)) return;
    const timer=setTimeout(()=> {
      this.delete(key);
      this.refreshTimers.delete(key);
    },duration);
    this.refreshTimers.set(key,timer);
  }

  getStats() {
    return {
      size: this.data.size,
      maxSize: CACHE_CONFIG.MAX_SIZE,
      hitRate: this.hitRate || 0
    };
  }
}

const cache=new Cache();

// Aftershock detection parameters
const AFTERSHOCK_PARAMETERS={
  maxTimeHours: 72,
  maxDistanceKm: 100,
  minMagnitudeDifference: 0.5
};

class CalamityService {
  constructor() {
    this.lastUpdate=null;
    this.refreshInterval=null;
    this.errorCount=0;
    this.requestController=new Map(); // Track active requests
  }

  async fetchWithCache(url,cacheKey,fetcher,duration) {
    // Check if request is already in progress
    if (this.requestController.has(cacheKey)) {
      return this.requestController.get(cacheKey);
    }
    
    // Check cache first
    const cached=cache.get(cacheKey);
    if (cached) {
      cache.hitRate=(cache.hitRate || 0) + 1;
      return cached;
    }
    
    // Create new request
    const promise=fetcher().finally(()=> {
      this.requestController.delete(cacheKey);
    });
    this.requestController.set(cacheKey,promise);
    cache.hitRate=(cache.hitRate || 0);

    try {
      const data=await promise;
      cache.set(cacheKey,data,duration);
      return data;
    } catch (error) {
      console.error(`Cache fetch error for ${cacheKey}:`,error);
      // Return stale data if available
      const staleData=cache.data.get(cacheKey);
      return staleData || [];
    }
  }

  async fetchVolcanicData() {
    const cacheKey='volcanic_data';
    return this.fetchWithCache(
      cacheKey,
      cacheKey,
      async ()=> generateVolcanicActivity(),
      CACHE_CONFIG.DURATION.VOLCANIC
    );
  }

  async fetchUSGSData(timeWindow='72h',minMagnitude=5.0) {
    const cacheKey=`usgs_${timeWindow}_${minMagnitude}`;
    return this.fetchWithCache(
      cacheKey,
      cacheKey,
      async ()=> {
        const startTime=this.getStartTime(timeWindow);
        const url=`https://earthquake.usgs.gov/fdsnws/event/1/query?format=geojson&starttime=${startTime}&minmagnitude=${minMagnitude}&minlatitude=${PH_BOUNDS.minlatitude}&maxlatitude=${PH_BOUNDS.maxlatitude}&minlongitude=${PH_BOUNDS.minlongitude}&maxlongitude=${PH_BOUNDS.maxlongitude}&orderby=time`;
        const response=await fetch(url,{signal: AbortSignal.timeout(10000) // 10 second timeout
        });
        if (!response.ok) throw new Error(`USGS API error: ${response.status}`);
        const data=await response.json();
        const normalized=this.normalizeUSGSData(data);
        this.errorCount=0;
        return normalized;
      },
      CACHE_CONFIG.DURATION.USGS
    );
  }

  async fetchGDACSData(timeWindow='72h') {
    const cacheKey=`gdacs_${timeWindow}`;
    return this.fetchWithCache(
      cacheKey,
      cacheKey,
      async ()=> {
        const fromDate=this.getStartTime(timeWindow).split('T')[0];
        const toDate=new Date().toISOString().split('T')[0];
        const endpoints=[
          `https://www.gdacs.org/gdacsapi/api/events/geteventlist/SEARCH?eventtype=EQ&fromdate=${fromDate}&todate=${toDate}`,
          `https://www.gdacs.org/gdacsapi/api/events/geteventlist/SEARCH?eventtype=TS&fromdate=${fromDate}&todate=${toDate}`,
          `https://www.gdacs.org/gdacsapi/api/events/geteventlist/SEARCH?eventtype=FL&fromdate=${fromDate}&todate=${toDate}`,
          `https://www.gdacs.org/gdacsapi/api/events/geteventlist/SEARCH?eventtype=TC&fromdate=${fromDate}&todate=${toDate}`
        ];
        const promises=endpoints.map(url=>
          fetch(url,{signal: AbortSignal.timeout(8000)})
            .then(res=> res.ok ? res.json() : null)
            .catch(()=> null)
        );
        const results=await Promise.allSettled(promises);
        let events=[];
        results.forEach((result,index)=> {
          if (result.status==='fulfilled' && result.value) {
            const eventType=['EQ','TS','FL','TC'][index];
            events=events.concat(this.normalizeGDACSData(result.value,eventType));
          }
        });
        const filtered=events.filter(event=> 
          event.lat >=PH_BOUNDS.minlatitude && event.lat <=PH_BOUNDS.maxlatitude &&
          event.lng >=PH_BOUNDS.minlongitude && event.lng <=PH_BOUNDS.maxlongitude
        );
        return filtered;
      },
      CACHE_CONFIG.DURATION.GDACS
    );
  }

  async fetchTidalData() {
    const cacheKey='tidal_data';
    return this.fetchWithCache(
      cacheKey,
      cacheKey,
      async ()=> this.generateTidalData(),
      CACHE_CONFIG.DURATION.TIDAL
    );
  }

  async fetchFloodData() {
    const cacheKey='flood_data';
    return this.fetchWithCache(
      cacheKey,
      cacheKey,
      async ()=> this.generateFloodData(),
      CACHE_CONFIG.DURATION.FLOOD
    );
  }

  async fetchPAGASAData() {
    const cacheKey='pagasa_data';
    return this.fetchWithCache(
      cacheKey,
      cacheKey,
      async ()=> this.generateWeatherAlerts(),
      CACHE_CONFIG.DURATION.PAGASA
    );
  }

  async getAllCalamities(timeWindow='72h',minMagnitude=5.0) {
    const promises=[
      this.fetchUSGSData(timeWindow,minMagnitude),
      this.fetchGDACSData(timeWindow),
      this.fetchTidalData(),
      this.fetchFloodData(),
      this.fetchPAGASAData(),
      this.fetchVolcanicData()
    ];
    const [usgsData,gdacsData,tidalData,floodData,pagasaData,volcanicData]=await Promise.allSettled(promises);
    
    const allEvents=[
      ...(usgsData.status==='fulfilled' ? usgsData.value : []),
      ...(gdacsData.status==='fulfilled' ? gdacsData.value : []),
      ...(tidalData.status==='fulfilled' ? tidalData.value : []),
      ...(floodData.status==='fulfilled' ? floodData.value : []),
      ...(pagasaData.status==='fulfilled' ? pagasaData.value : []),
      ...(volcanicData.status==='fulfilled' ? volcanicData.value : [])
    ];
    
    const eventsWithAftershocks=this.classifyAftershocks(allEvents);
    const uniqueEvents=this.deduplicateEvents(eventsWithAftershocks);
    uniqueEvents.sort((a,b)=> new Date(b.time_utc) - new Date(a.time_utc));
    this.lastUpdate=new Date();
    
    return {
      events: uniqueEvents,
      lastUpdate: this.lastUpdate,
      sources: {
        usgs: usgsData.status==='fulfilled' && usgsData.value.length > 0,
        gdacs: gdacsData.status==='fulfilled' && gdacsData.value.length > 0,
        pagasa: pagasaData.status==='fulfilled' && pagasaData.value.length > 0,
        tidal: tidalData.status==='fulfilled' && tidalData.value.length > 0,
        flood: floodData.status==='fulfilled' && floodData.value.length > 0,
        phivolcs: volcanicData.status==='fulfilled' && volcanicData.value.length > 0
      }
    };
  }

  classifyAftershocks(events) {
    const sortedEvents=[...events].sort((a,b)=> new Date(a.time_utc) - new Date(b.time_utc) );
    const eventMap=new Map(sortedEvents.map(e=> [e.id,e]));

    sortedEvents.forEach(event=> {
      if (event.type !=='earthquake') return;
      
      let isAftershock=false;
      let parentMainShock=null;

      for (const mainShock of eventMap.values()) {
        if (mainShock.id===event.id || mainShock.type !=='earthquake') continue;

        const timeDiff=(new Date(event.time_utc) - new Date(mainShock.time_utc)) / (1000 * 60 * 60);
        if (timeDiff > 0 && timeDiff <=AFTERSHOCK_PARAMETERS.maxTimeHours) {
          const distance=this.calculateDistance(
            mainShock.lat,mainShock.lng,
            event.lat,event.lng
          );
          if (distance <=AFTERSHOCK_PARAMETERS.maxDistanceKm && mainShock.magnitude >=event.magnitude + AFTERSHOCK_PARAMETERS.minMagnitudeDifference) {
            if (!parentMainShock || mainShock.magnitude > parentMainShock.magnitude) {
              parentMainShock=mainShock;
              isAftershock=true;
            }
          }
        }
      }
      
      if (isAftershock && parentMainShock) {
        event.aftershock_of=parentMainShock.id;
        event.earthquake_type='aftershock';
        
        const parentInMap=eventMap.get(parentMainShock.id);
        if (parentInMap) {
          parentInMap.earthquake_type='main_shock';
          if (!parentInMap.aftershocks) parentInMap.aftershocks=[];
          parentInMap.aftershocks.push(event.id);
        }
      } else if (event.magnitude >=5.0) {
        if (!event.earthquake_type) event.earthquake_type='main_shock';
      } else {
        if (!event.earthquake_type) event.earthquake_type='independent';
      }
    });
    
    return Array.from(eventMap.values());
  }

  calculateDistance(lat1,lon1,lat2,lon2) {
    const R=6371;
    const dLat=(lat2 - lat1) * Math.PI / 180;
    const dLon=(lon2 - lon1) * Math.PI / 180;
    const a=
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c=2 * Math.atan2(Math.sqrt(a),Math.sqrt(1 - a));
    return R * c;
  }

  generateTidalData() {
    const now=new Date();
    const tidalEvents=[];
    const coastalCities=[
      {name: 'Manila Bay',lat: 14.5833,lng: 120.9667},
      {name: 'Cebu City',lat: 10.3157,lng: 123.8854},
      {name: 'Davao City',lat: 7.1907,lng: 125.4553}
    ];

    coastalCities.forEach(city=> {
      for (let i=0;i < 4;i++) {
        const isHighTide=i % 2===0;
        const eventTime=new Date(now.getTime() + (i * 6 * 60 * 60 * 1000));
        tidalEvents.push({
          id: `tidal:${city.name.toLowerCase().replace(' ','-')}-${i}`,
          source: 'pagasa',
          type: 'tidal',
          event_type: isHighTide ? 'high_tide' : 'low_tide',
          time_utc: eventTime.toISOString(),
          magnitude: isHighTide ? 1.2 + Math.random() * 0.6 : -0.3 - Math.random() * 0.3,
          lat: city.lat,
          lng: city.lng,
          place_text: `${isHighTide ? 'High' : 'Low'} Tide - ${city.name}`,
          nearest_city: findNearestCity(city.lat,city.lng),
          province: this.getProvince(city.lat,city.lng),
          alert: this.determineTidalAlert(isHighTide,Math.random()),
          tsunami: null,
          tidal_info: {
            height_m: (isHighTide ? (1.2 + Math.random() * 0.6) : (-0.3 - Math.random() * 0.3)).toFixed(2),
            type: isHighTide ? 'High' : 'Low',
            next_tide: new Date(eventTime.getTime() + (6 * 60 * 60 * 1000)).toISOString()
          }
        });
      }
    });
    return tidalEvents;
  }

  generateFloodData() {
    const floodEvents=[];
    const now=new Date();
    const floodProneAreas=[
      {name: 'Marikina River',lat: 14.6506,lng: 121.1070,severity: 'moderate'},
      {name: 'Cagayan River',lat: 17.6158,lng: 121.7260,severity: 'high'}
    ];

    floodProneAreas.forEach(area=> {
      if (Math.random() > 0.3) {
        floodEvents.push({
          id: `flood:${area.name.toLowerCase().replace(' ','-')}`,
          source: 'pagasa',
          type: 'flood',
          event_type: 'flood',
          time_utc: new Date(now.getTime() - Math.random() * 24 * 60 * 60 * 1000).toISOString(),
          magnitude: area.severity==='high' ? 8 + Math.random() * 2 : 5 + Math.random() * 3,
          lat: area.lat + (Math.random() - 0.5) * 0.5,
          lng: area.lng + (Math.random() - 0.5) * 0.5,
          place_text: `Flood Warning - ${area.name}`,
          nearest_city: findNearestCity(area.lat,area.lng),
          province: this.getProvince(area.lat,area.lng),
          alert: area.severity==='high' ? 'Warning' : 'Watch',
          tsunami: null,
          flood_info: {
            severity: area.severity,
            water_level_m: (area.severity==='high' ? 3 + Math.random() * 2 : 1.5 + Math.random() * 1.5).toFixed(2),
            affected_area: `${(5 + Math.random() * 20).toFixed(1)} sq km`,
            evacuation_advised: area.severity==='high'
          }
        });
      }
    });
    return floodEvents;
  }

  generateWeatherAlerts() {
    const weatherEvents=[];
    const now=new Date();
    const weatherSystems=[
      {type: 'tropical_depression',name: 'TD 01W',lat: 11.5,lng: 126.5,severity: 'moderate'},
      {type: 'monsoon',name: 'Southwest Monsoon',lat: 14.0,lng: 120.0,severity: 'high'}
    ];

    weatherSystems.forEach(system=> {
      weatherEvents.push({
        id: `weather:${system.type.toLowerCase()}-${Date.now()}`,
        source: 'pagasa',
        type: 'weather',
        event_type: system.type,
        time_utc: new Date(now.getTime() - Math.random() * 48 * 60 * 60 * 1000).toISOString(),
        magnitude: system.severity==='high' ? 7 + Math.random() * 3 : 4 + Math.random() * 3,
        lat: system.lat + (Math.random() - 0.5) * 2,
        lng: system.lng + (Math.random() - 0.5) * 2,
        place_text: `${system.name} - Weather Alert`,
        nearest_city: findNearestCity(system.lat,system.lng),
        province: this.getProvince(system.lat,system.lng),
        alert: system.severity==='high' ? 'Warning' : 'Watch',
        tsunami: null,
        weather_info: {
          system_type: system.type,
          wind_speed_kph: Math.floor(20 + Math.random() * 80),
          rainfall_mm: Math.floor(50 + Math.random() * 200),
          signal_number: system.severity==='high' ? Math.floor(1 + Math.random() * 3) : 0
        }
      });
    });
    return weatherEvents;
  }

  determineTidalAlert(isHighTide,random) {
    if (isHighTide && random > 0.8) return 'Advisory';
    return 'None';
  }

  normalizeUSGSData(data) {
    return data.features.map(feature=> {
      const {properties: props,geometry: geo}=feature;
      const [lng,lat,depth]=geo.coordinates;
      return {
        id: `usgs:${feature.id}`,
        source: 'usgs',
        type: 'earthquake',
        event_type: 'earthquake',
        time_utc: new Date(props.time).toISOString(),
        magnitude: props.mag,
        depth_km: depth || 0,
        lat,
        lng,
        place_text: props.place,
        nearest_city: findNearestCity(lat,lng),
        province: this.getProvince(lat,lng),
        alert: this.determineAlert(props,'usgs'),
        tsunami: this.checkTsunami(props,geo.coordinates),
        detail_url: props.detail
      };
    });
  }

  normalizeGDACSData(data,eventType) {
    if (!data || !Array.isArray(data)) return [];
    return data.map(event=> {
      const lat=parseFloat(event.latitude) || 0;
      const lng=parseFloat(event.longitude) || 0;
      return {
        id: `gdacs:${event.eventid}`,
        source: 'gdacs',
        type: this.mapGDACSType(eventType),
        event_type: event.eventtype,
        time_utc: new Date(event.datemodified).toISOString(),
        magnitude: parseFloat(event.severitydata?.magnitude) || 0,
        depth_km: parseFloat(event.depth) || 0,
        lat,
        lng,
        place_text: event.title,
        nearest_city: findNearestCity(lat,lng),
        province: this.getProvince(lat,lng),
        alert: this.mapGDACSAlert(event.alertlevel),
        tsunami: {
          has_warning: eventType==='TS',
          source: 'gdacs',
          level: eventType==='TS' ? this.mapGDACSAlert(event.alertlevel) : 'None'
        }
      };
    });
  }
  
  mapGDACSType(eventType) {
    return {
      EQ: 'earthquake',
      TS: 'tsunami',
      FL: 'flood',
      TC: 'weather'
    }[eventType] || 'other';
  }

  deduplicateEvents(events) {
    const uniqueEvents=new Map();
    events.forEach(event=> {
      const key=`${event.source}-${event.id}`;
      if (!uniqueEvents.has(key)) {
        uniqueEvents.set(key,event);
      }
    });
    return Array.from(uniqueEvents.values());
  }
  
  determineAlert(props,source) {
    if (source==='usgs') {
      if (props.tsunami===1) return 'Watch';
      return {
        red: 'Warning',
        orange: 'Watch',
        yellow: 'Advisory'
      }[props.alert] || 'None';
    }
    return 'None';
  }
  
  mapGDACSAlert(alertLevel) {
    return {
      red: 'Warning',
      orange: 'Watch',
      green: 'Watch'
    }[alertLevel?.toLowerCase()] || 'None';
  }

  checkTsunami(props,coords) {
    const hasWarning=props.tsunami===1;
    return {
      has_warning: hasWarning,
      source: hasWarning ? 'usgs' : null,
      level: hasWarning ? 'Watch' : 'None'
    };
  }

  getProvince(lat) {
    if (lat >=18) return 'Luzon (North)';
    if (lat >=14) return 'Luzon (Central)';
    if (lat >=12) return 'Luzon (South)';
    if (lat >=8) return 'Visayas';
    return 'Mindanao';
  }

  getStartTime(timeWindow) {
    const now=new Date();
    const duration={
      '24h': 24,
      '48h': 48,
      '72h': 72,
      '7d': 7 * 24,
      '30d': 30 * 24
    }[timeWindow] || 72;
    return new Date(now.getTime() - duration * 60 * 60 * 1000).toISOString();
  }
  
  startAutoRefresh(callback,interval=60000) {
    if (this.refreshInterval) clearInterval(this.refreshInterval);
    this.refreshInterval=setInterval(callback,interval);
  }

  stopAutoRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval=null;
    }
  }

  getHealthStatus() {
    return {
      lastUpdate: this.lastUpdate,
      errorCount: this.errorCount,
      cacheSize: cache.data.size,
      cacheStats: cache.getStats(),
      sources: {
        usgs: this.errorCount < 3,
        gdacs: true,
        pagasa: true,
        tidal: true,
        flood: true,
        phivolcs: true
      }
    };
  }

  // Clear cache manually if needed
  clearCache() {
    cache.data.clear();
    cache.timestamps.clear();
    this.requestController.forEach(controller=> {
      if (controller.abort) controller.abort();
    });
    this.requestController.clear();
  }
}

export default new CalamityService();